import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { getInitials, getRandomColor, shouldShowApiToast } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getDevotionals,
  getDevotional,
  createDevotional,
  updateDevotional,
  deleteDevotional,
  duplicateDevotional,
  toggleDevotional,
  sendDevotional,
  getDevotionalSubscribers,
  addDevotionalSubscriber,
  removeDevotionalSubscriber,
  getDevotionalStats,
  exportDevotionals,
  clearApiMessage,
  clearError,
  clearDevotionalState,
  clearCurrentDevotional,
  clearSubscribers,
  selectDevotionals,
  selectCurrentDevotional,
  selectDevotionalSubscribers,
  selectDevotionalStats,
  selectDevotionalPagination,
  selectDevotionalLoading,
  selectDevotionalError,
  selectDevotionalApiMessage,
  selectDevotionalSuccess,
  selectDevotionalExportData,
  selectDevotionalStatsSummary,
} from '../../store/slices/engagement/devotionalSlice';
import {
  Download,
  BarChart3,
  FileJson,
  TrendingUp,
  FileText,
  CheckCircle2,
  ClipboardList,
  BookOpen,
  Pencil,
  Save,
  Trash2,
  AlertTriangle,
  Send,
  Bell,
  Users,
  Calendar,
  Search,
  Sparkles,
  Copy,
  FileEdit,
  Loader2,
} from 'lucide-react';

const Devotionals = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSeriesId, setEditingSeriesId] = useState(null);

  // Refs for edit form
  const editTitleRef = useRef(null);
  const editScriptureRef = useRef(null);
  const editTextRef = useRef(null);
  const editFrequencyRef = useRef(null);
  const editDateTimeRef = useRef(null);
  const editKeywordRef = useRef(null);
  const editMessageRef = useRef(null);

  // Refs for create form
  const createTitleRef = useRef(null);
  const createScriptureRef = useRef(null);
  const createTextRef = useRef(null);
  const createFrequencyRef = useRef(null);
  const createDateTimeRef = useRef(null);
  const createKeywordRef = useRef(null);
  const createMessageRef = useRef(null);

  // Refs for opt-in setup
  const optInMessageRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const devotionals = useSelector(selectDevotionals) || [];
  const currentDevotional = useSelector(selectCurrentDevotional);
  const subscribers = useSelector(selectDevotionalSubscribers) || [];
  const stats = useSelector(selectDevotionalStatsSummary);
  const pagination = useSelector(selectDevotionalPagination);
  const loading = useSelector(selectDevotionalLoading);
  const apiMessage = useSelector(selectDevotionalApiMessage);
  const error = useSelector(selectDevotionalError);
  const successMessage = useSelector(selectDevotionalSuccess);
  const exportData = useSelector(selectDevotionalExportData);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getDevotionals({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: statusFilter
        }));
        await dispatch(getDevotionalStats());
      } catch (err) {
        console.error('Error loading devotionals:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearDevotionalState());
    };
  }, [dispatch]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getDevotionals(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast || !shouldShowApiToast(message, !!error)) return;
    
    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPERS ====================

  const getFormattedSchedule = (devotional) => {
    if (!devotional.frequency) return 'Not scheduled';
    
    const frequencyMap = {
      daily: 'Daily',
      weekly: 'Weekly',
      weekdays: 'Weekdays only',
      custom: 'Custom days'
    };
    
    const frequency = frequencyMap[devotional.frequency] || devotional.frequency;
    let formattedTime = '';
    if (devotional.send_time) {
      const date = new Date(devotional.send_time);
      if (!isNaN(date.getTime())) {
        formattedTime = date.toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit', 
          minute: '2-digit'
        });
      }
    }
    
    return frequency + (formattedTime ? ` at ${formattedTime}` : '');
  };

  const getDefaultDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };

  const formatDateTimeForInput = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 16);
  };

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExport = async (type, devotionalId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      const params = {};
      if (devotionalId) params.devotional_id = devotionalId;
      
      const result = await dispatch(exportDevotionals({ type, params })).unwrap();
      console.log('Export result:', result);
      
      if (result.success && result.exportData) {
        let data = result.exportData;
        
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } 
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            data = Object.values(data);
          }
        }
        
        if (!Array.isArray(data) || data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }
        
        const filename = getExportFilename(`devotionals-${type}`);
        
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Devotionals Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }
        
        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'all', seriesItem = null) => {
    let data = [];
    let title = 'Devotionals Report';
    let filename = 'devotionals-report';

    if (exportType === 'single' && seriesItem) {
      data = [{
        'Series Name': seriesItem.title,
        'Scripture': seriesItem.scripture,
        'Text': seriesItem.text,
        'Status': seriesItem.active ? 'Active' : 'Inactive',
        'Subscribers': seriesItem.subscribers || 0,
        'Schedule': getFormattedSchedule(seriesItem),
        'ID': seriesItem.id
      }];
      title = `Devotional Series: ${seriesItem.title}`;
      filename = `devotional-${seriesItem.id}-${seriesItem.title.substring(0, 30)}`;
    } else if (exportType === 'subscribers' && seriesItem) {
      const subscriberList = subscribers.length > 0 ? subscribers : [];
      data = subscriberList.map(s => ({
        'Subscriber': s.member?.name || s.phone || 'Anonymous',
        'Series': seriesItem.title,
        'Subscribed Date': s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : 'N/A',
        'Status': s.is_active ? 'Active' : 'Inactive'
      }));
      title = `Subscribers: ${seriesItem.title}`;
      filename = `subscribers-${seriesItem.id}`;
    } else if (exportType === 'summary') {
      const totalSubscribers = devotionals.reduce((a, b) => a + (b.subscribers || 0), 0);
      const activeSeries = devotionals.filter(d => d.active).length;
      
      data = devotionals.map(d => ({
        'Series Name': d.title,
        'Status': d.active ? 'Active' : 'Inactive',
        'Subscribers': d.subscribers || 0,
        'Schedule': getFormattedSchedule(d),
        'Scripture': d.scripture
      }));
      
      data.push({
        'Series Name': '=== TOTALS ===',
        'Status': '',
        'Subscribers': totalSubscribers,
        'Schedule': `${activeSeries} active series`,
        'Scripture': ''
      });
      
      title = 'Devotionals Summary Report';
      filename = 'devotionals-summary';
    } else {
      data = devotionals.map(d => ({
        'ID': d.id,
        'Series Name': d.title,
        'Scripture': d.scripture,
        'Text': d.text,
        'Status': d.active ? 'Active' : 'Inactive',
        'Subscribers': d.subscribers || 0,
        'Schedule': getFormattedSchedule(d)
      }));
      title = 'All Devotional Series';
      filename = 'all-devotionals';
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
            {exportType === 'single' && seriesItem && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {seriesItem.title}
              </span>
            )}
            {exportType === 'subscribers' && seriesItem && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · Subscribers of {seriesItem.title}
              </span>
            )}
            {exportType === 'summary' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · Summary of {devotionals.length} series
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, seriesItem?.id, format)}
                  disabled={isSubmitting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <FileJson size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {data.length} records will be exported</span>
          </div>

          {/* Quick Export Options */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Quick Export Options
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => handleExport('active')}
                disabled={isSubmitting}
              >
                <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Active Only
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handleExport('summary')}
                disabled={isSubmitting}
              >
                <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Summary
              </button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAll = () => {
    if (devotionals.length === 0) {
      showToastMessage('No devotional series to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportSeries = (seriesItem) => {
    openExportModal('single', seriesItem);
  };

  const handleExportSubscribers = async (seriesItem) => {
    if (seriesItem.subscribers === 0) {
      showToastMessage('No subscribers to export', 'info');
      return;
    }
    
    try {
      setIsSubmitting(true);
      await dispatch(getDevotionalSubscribers({ id: seriesItem.id, params: { per_page: 100 } }));
      openExportModal('subscribers', seriesItem);
    } catch (err) {
      showToastMessage(err.message || 'Failed to load subscribers', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportSummary = () => {
    if (devotionals.length === 0) {
      showToastMessage('No devotional series to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== DEVOTIONAL CRUD ====================

  const handleToggleSeries = async (id, active) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(toggleDevotional(id)).unwrap();
      if (result.success) {
        await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getDevotionalStats());
        const seriesName = devotionals.find(s => s.id === id)?.title || 'Series';
        showToastMessage(`📖 "${seriesName}" ${active ? 'activated' : 'paused'}`, active ? 'success' : 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle devotional', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateDevotional = () => {
    const defaultDateTime = getDefaultDateTime();
    
    setTimeout(() => {
      if (createTitleRef.current) createTitleRef.current.value = '';
      if (createScriptureRef.current) createScriptureRef.current.value = '';
      if (createTextRef.current) createTextRef.current.value = '';
      if (createFrequencyRef.current) createFrequencyRef.current.value = 'daily';
      if (createDateTimeRef.current) createDateTimeRef.current.value = defaultDateTime;
      if (createKeywordRef.current) createKeywordRef.current.value = '';
      if (createMessageRef.current) createMessageRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BookOpen size={16} style={{ verticalAlign: '-3px' }} /> New Devotional Series</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Series Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              placeholder="Monday Motivation"
              ref={createTitleRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Scripture Reference <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              placeholder="Philippians 4:13"
              ref={createScriptureRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Scripture Text <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="2"
              ref={createTextRef}
              placeholder="I can do all things through Christ who strengthens me."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Frequency</label>
              <select 
                className="form-select"
                ref={createFrequencyRef}
                defaultValue="daily"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="weekdays">Weekdays only</option>
                <option value="custom">Custom days</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Send Date & Time</label>
              <input 
                className="form-input" 
                type="datetime-local"
                ref={createDateTimeRef}
                defaultValue={defaultDateTime}
              />
              <div className="form-hint">Leave blank to send immediately</div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Opt-In Keyword</label>
            <input 
              className="form-input" 
              placeholder="DAILY, DEVOTION, BLESS…"
              ref={createKeywordRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sample Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={createMessageRef}
              placeholder="Good morning {FirstName}! 🌅 Today's scripture: ... Have a blessed day!"
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = createTitleRef.current?.value?.trim() || '';
              const scripture = createScriptureRef.current?.value?.trim() || '';
              const text = createTextRef.current?.value?.trim() || '';
              const frequency = createFrequencyRef.current?.value || 'daily';
              const sendTime = createDateTimeRef.current?.value || null;
              const keyword = createKeywordRef.current?.value?.trim() || '';
              const messageTemplate = createMessageRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter a series name', 'error');
                return;
              }
              if (!scripture) {
                showToastMessage('Please enter a scripture reference', 'error');
                return;
              }
              if (!text) {
                showToastMessage('Please enter the scripture text', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createDevotional({
                  title,
                  scripture,
                  text,
                  frequency,
                  send_time: sendTime,
                  keyword,
                  message_template: messageTemplate,
                  active: false
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getDevotionalStats());
                  showToastMessage(`📖 "${title}" created successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to create devotional', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Series'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditSeries = (d) => {
    setEditingSeriesId(d.id);
    
    setTimeout(() => {
      if (editTitleRef.current) editTitleRef.current.value = d.title || '';
      if (editScriptureRef.current) editScriptureRef.current.value = d.scripture || '';
      if (editTextRef.current) editTextRef.current.value = d.text || '';
      if (editFrequencyRef.current) editFrequencyRef.current.value = d.frequency || 'daily';
      if (editDateTimeRef.current) {
        editDateTimeRef.current.value = formatDateTimeForInput(d.send_time);
      }
      if (editKeywordRef.current) editKeywordRef.current.value = d.keyword || '';
      if (editMessageRef.current) editMessageRef.current.value = d.message_template || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Series: {d.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Series Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editTitleRef}
              defaultValue={d.title || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Scripture Reference <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editScriptureRef}
              defaultValue={d.scripture || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Scripture Text <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="2"
              ref={editTextRef}
              defaultValue={d.text || ''}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Frequency</label>
              <select 
                className="form-select"
                ref={editFrequencyRef}
                defaultValue={d.frequency || 'daily'}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="weekdays">Weekdays only</option>
                <option value="custom">Custom days</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Send Date & Time</label>
              <input 
                className="form-input" 
                type="datetime-local"
                ref={editDateTimeRef}
                defaultValue={formatDateTimeForInput(d.send_time)}
              />
              <div className="form-hint">Leave blank to send immediately</div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Opt-In Keyword</label>
            <input 
              className="form-input" 
              placeholder="DAILY, DEVOTION, BLESS…"
              ref={editKeywordRef}
              defaultValue={d.keyword || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sample Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={editMessageRef}
              defaultValue={d.message_template || ''}
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = editTitleRef.current?.value?.trim() || '';
              const scripture = editScriptureRef.current?.value?.trim() || '';
              const text = editTextRef.current?.value?.trim() || '';
              const frequency = editFrequencyRef.current?.value || 'daily';
              const sendTime = editDateTimeRef.current?.value || null;
              const keyword = editKeywordRef.current?.value?.trim() || '';
              const messageTemplate = editMessageRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter a series name', 'error');
                return;
              }
              if (!scripture) {
                showToastMessage('Please enter a scripture reference', 'error');
                return;
              }
              if (!text) {
                showToastMessage('Please enter the scripture text', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateDevotional({
                  id: d.id,
                  data: {
                    title,
                    scripture,
                    text,
                    frequency,
                    send_time: sendTime,
                    keyword,
                    message_template: messageTemplate
                  }
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getDevotionalStats());
                  showToastMessage(`✅ "${title}" updated successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update devotional', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteSeries = (d) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Devotional Series</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{d.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the series and all subscriber data.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteDevotional(d.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getDevotionalStats());
                  showToastMessage(`🗑️ "${d.title}" deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete devotional', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Series
          </button>
        </div>
      </div>
    );
  };

  const handleDuplicateSeries = async (d) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(duplicateDevotional(d.id)).unwrap();
      if (result.success) {
        await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getDevotionalStats());
        showToastMessage(`📋 "${result.devotional?.title || d.title}" created`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate devotional', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendDevotional = async (d) => {
    if (!d.active) {
      showToastMessage('Please activate the devotional first', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Send Devotional: {d.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to send <strong>{d.title}</strong> to {d.subscribers || 0} subscribers?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--text-mid)' 
          }}>
            <div><BookOpen size={13} style={{ verticalAlign: '-2px' }} /> Series: <strong>{d.title}</strong></div>
            <div><BarChart3 size={13} style={{ verticalAlign: '-2px' }} /> Subscribers: <strong>{d.subscribers || 0}</strong></div>
            {d.frequency && (
              <div><Calendar size={13} style={{ verticalAlign: '-2px' }} /> Schedule: <strong>{getFormattedSchedule(d)}</strong></div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(sendDevotional(d.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getDevotionals({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getDevotionalStats());
                  showToastMessage(`📨 Devotional sent to ${result.result?.sent || 0} subscribers!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send devotional', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Sending...' : <><Send size={14} style={{ verticalAlign: '-2px' }} /> Send Now</>}
          </button>
        </div>
      </div>
    );
  };

  // Preview Devotional Series
  const handlePreview = (d) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Bell size={16} style={{ verticalAlign: '-3px' }} /> Preview: {d.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--navy)', borderRadius: '12px', padding: '20px', color: 'white' }}>
            <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '8px', fontWeight: '600' }}>GRACECHAPEL</div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{d.title}</div>
            <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '8px' }}>{d.scripture}</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.85)', lineHeight: '1.6', fontStyle: 'italic' }}>"{d.text}"</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.3)', marginTop: '12px' }}>
              Sent to {d.subscribers || 0} subscribers · {getFormattedSchedule(d)}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              showToastMessage(`📨 Test SMS sent to your phone!`, 'success');
              closeModal();
            }}
          >
            <Send size={14} style={{ verticalAlign: '-2px' }} /> Send Test SMS
          </button>
        </div>
      </div>
    );
  };

  // Manage Subscribers
  const handleManageSubscribers = async (d) => {
    try {
      setIsSubmitting(true);
      await dispatch(getDevotionalSubscribers({ id: d.id, params: { per_page: 50 } }));
    } catch (err) {
      showToastMessage(err.message || 'Failed to load subscribers', 'error');
    } finally {
      setIsSubmitting(false);
    }
    
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px' }} /> Subscribers: {d.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Total Subscribers</div>
                <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{d.subscribers || 0}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  className="btn btn-ghost btn-sm" 
                  onClick={() => handleExportSubscribers(d)}
                  disabled={isSubmitting}
                >
                  <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => handleSendDevotional(d)}
                  disabled={isSubmitting}
                >
                  <Send size={14} style={{ verticalAlign: '-2px' }} /> Send Broadcast
                </button>
              </div>
            </div>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: 'var(--text-light)' }}>Loading subscribers...</div>
            </div>
          ) : subscribers.length > 0 ? (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {subscribers.map((s, index) => {
                const colors = ['#4f46e5', '#7c3aed', '#dc2626', '#059669', '#d97706'];
                const color = colors[index % colors.length];
                const initials = s.member?.name ? getInitials(s.member.name) : s.phone?.slice(0, 2) || '??';
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <div className="member-row-avatar" style={{ background: color, width: '28px', height: '28px', fontSize: '11px' }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: '500' }}>{s.member?.name || s.phone || 'Unknown'}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>
                        Subscribed {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <button 
                      className="btn btn-ghost btn-sm" 
                      onClick={() => showToastMessage('📨 SMS sent', 'success')}
                      disabled={isSubmitting}
                    >
                      <Send size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: 'var(--text-light)' }}>No subscribers yet</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Save Opt-In Settings
  const handleSaveOptIn = () => {
    showToastMessage('💾 Opt-in message saved!', 'success');
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    totalDevotionals: 0,
    activeDevotionals: 0,
    inactiveDevotionals: 0,
    totalSubscribers: 0,
    sentThisMonth: 0,
    optInRate: 0,
    byFrequency: {}
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <BookOpen size={16} style={{ verticalAlign: '-3px' }} /> Send automated daily scripture, devotions, or inspirational messages to opted-in members.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export devotional summary"
            disabled={isSubmitting}
          >
            <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all devotional series"
            disabled={isSubmitting}
          >
            <Download size={14} style={{ verticalAlign: '-2px' }} /> Export All
          </button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={openCreateDevotional} 
            title="Create new devotional series"
            disabled={isSubmitting}
          >
            + New Devotional Series
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Active Series</div>
          <div className="stat-value">{statsData.activeDevotionals}</div>
          <div className="stat-change up">running</div>
          <div className="stat-icon"><BookOpen size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Subscribers</div>
          <div className="stat-value">{statsData.totalSubscribers}</div>
          <div className="stat-change up">opted in</div>
          <div className="stat-icon"><CheckCircle2 size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Sent This Month</div>
          <div className="stat-value">{statsData.sentThisMonth}</div>
          <div className="stat-change up">devotional msgs</div>
          <div className="stat-icon"><Send size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Opt-In Rate</div>
          <div className="stat-value">{statsData.optInRate}%</div>
          <div className="stat-change up">of congregation</div>
          <div className="stat-icon"><Sparkles size={28} strokeWidth={1.5} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input 
              type="text" 
              placeholder="Search series…" 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search by title, scripture, or text"
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by status"
          >
            <option value="">All Series</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {devotionals.length} series found
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        {loading && devotionals.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
            <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading devotionals...</div>
          </div>
        ) : devotionals.length > 0 ? (
          devotionals.map(d => (
            <div key={d.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--gold-pale)', border: '1px solid #f0dfa0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}><BookOpen size={20} /></div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>{d.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{getFormattedSchedule(d)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <label className="toggle" title={d.active ? 'Click to pause' : 'Click to activate'}>
                    <input 
                      type="checkbox" 
                      checked={d.active} 
                      onChange={(e) => handleToggleSeries(d.id, e.target.checked)} 
                      disabled={isSubmitting}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleExportSeries(d)}
                    title={`📥 Export ${d.title}`}
                    disabled={isSubmitting}
                  >
                    <Download size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleEditSeries(d)}
                    title={`✏️ Edit ${d.title}`}
                    disabled={isSubmitting}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDeleteSeries(d)}
                    title={`🗑️ Delete ${d.title}`}
                    style={{ color: 'var(--red)' }}
                    disabled={isSubmitting}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{ background: 'var(--navy)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '4px', fontWeight: '600' }}>{d.scripture}</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.85)', lineHeight: '1.6', fontStyle: 'italic' }}>"{d.text}"</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                  <strong style={{ color: 'var(--navy)' }}>{d.subscribers || 0}</strong> subscribers
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-ghost btn-sm" 
                    onClick={() => handlePreview(d)}
                    title={`🔔 Preview ${d.title}`}
                    disabled={isSubmitting}
                  >
                    <Bell size={14} style={{ verticalAlign: '-2px' }} /> Preview
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleManageSubscribers(d)}
                    title={`👥 Manage subscribers for ${d.title}`}
                    disabled={isSubmitting}
                  >
                    <Users size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleDuplicateSeries(d)}
                    title={`📋 Duplicate ${d.title}`}
                    disabled={isSubmitting}
                  >
                    <Copy size={14} style={{ verticalAlign: '-2px' }} /> Copy
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleSendDevotional(d)}
                    title={`📨 Send ${d.title} to subscribers`}
                    disabled={isSubmitting}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <div className="empty-icon"><BookOpen size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">No devotional series found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          </div>
        )}
        
        {/* Add New Series Card */}
        <div 
          className="card" 
          style={{ border: '2px dashed var(--border)', boxShadow: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }} 
          onClick={openCreateDevotional}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.4 }}>+</div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-mid)' }}>Add New Series</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Daily devotionals, weekly blessings…</div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> series
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Items per page"
                disabled={loading}
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
                <option value={6}>6</option>
                <option value={10}>10</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToPreviousPage}
                disabled={currentPageNum === 1 || loading}
                style={{ 
                  opacity: currentPageNum === 1 ? 0.5 : 1, 
                  cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                }}
                title="Previous page"
              >
                ← Prev
              </button>
              
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  title={`Go to page ${page}`}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToNextPage}
                disabled={currentPageNum === totalPages || loading}
                style={{ 
                  opacity: currentPageNum === totalPages ? 0.5 : 1, 
                  cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                }}
                title="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title"><FileEdit size={16} style={{ verticalAlign: '-3px' }} /> Devotional Opt-In Setup</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', borderRadius: '12px', padding: '18px', color: 'white', marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', opacity: 0.5, marginBottom: '6px' }}>Members opt in by texting:</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '20px', fontWeight: '600', color: 'var(--gold-light)' }}>DAILY → 40404</div>
              <div style={{ fontSize: '12px', opacity: 0.5, marginTop: '6px' }}>or SCRIPTURE, DEVOTION, BLESS</div>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', lineHeight: '1.7' }}>
              Members can also text <strong>STOP DAILY</strong> to unsubscribe from the devotional series at any time.
            </div>
          </div>
          <div>
            <div className="form-group">
              <label className="form-label">Opt-In Confirmation SMS</label>
              <textarea 
                className="form-textarea" 
                rows="3" 
                ref={optInMessageRef}
                defaultValue="You're subscribed to {SeriesName}! 🙏 You will receive messages {Schedule}. Text STOP DAILY anytime to unsubscribe. God bless! — Grace Chapel"
              />
              <div className="form-hint">Use {'{SeriesName}'} and {'{Schedule}'}</div>
            </div>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={handleSaveOptIn} 
              title="Save opt-in message"
              disabled={isSubmitting}
            >
              <Save size={14} style={{ verticalAlign: '-2px' }} /> Save
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
      `}</style>
    </>
  );
};

export default Devotionals;